/**
 * Phase 7 / E2 — client-side perf metrics beacon.
 *
 * Periodically POSTs a snapshot of `profiler.dump()` + a small page
 * fingerprint to `/metrics` so the autonomous loop / a Grafana endpoint
 * can analyze real-user perf without us manually pasting console output.
 *
 * Opt-in. Activated only when:
 *   - the profiler is on (`?perf=1` or localStorage.perfProfiler='1'), AND
 *   - the user hasn't set `localStorage.metricsBeacon === '0'`.
 *
 * Sends one beacon every 60 s + one final beacon on `pagehide`.
 *
 * The endpoint is on the same origin (no CORS / cookies needed). Failures
 * are silent — perf telemetry must never break the page.
 */

import { profiler } from './perfProfiler'

const BEACON_INTERVAL_MS = 60_000

const beaconEnabled = (() => {
  if (!profiler.enabled) return false
  try {
    if (typeof localStorage !== 'undefined' && localStorage.metricsBeacon === '0') return false
  } catch { /* ssr */ }
  return true
})()

type Beacon = {
  v: 1
  session: string
  bootMs: number
  renderer: string | null
  ua: string
  hardware: { cores: number; mem: number | null; dpr: number; w: number; h: number }
  url: { path: string; search: string }
  metrics: Record<string, { count: number; p50: number; p95: number; p99: number; max: number; mean: number }>
  milestones: Record<string, number>
}

const sessionId = () => {
  const ar = new Uint8Array(8)
  crypto.getRandomValues(ar)
  return Array.from(ar, b => b.toString(16).padStart(2, '0')).join('')
}

let session: string | undefined
const t0 = performance.now()

const buildBeacon = (): Beacon => {
  if (!session) session = sessionId()
  const params = typeof location !== 'undefined' ? new URLSearchParams(location.search) : null
  return {
    v: 1,
    session,
    bootMs: Math.round(performance.now() - t0),
    renderer: params?.get('renderer') ?? 'threejs',
    ua: navigator.userAgent,
    hardware: {
      cores: navigator.hardwareConcurrency ?? 0,
      mem: (navigator as any).deviceMemory ?? null,
      dpr: window.devicePixelRatio || 1,
      w: window.innerWidth,
      h: window.innerHeight,
    },
    url: {
      path: typeof location !== 'undefined' ? location.pathname : '',
      search: typeof location !== 'undefined' ? location.search : '',
    },
    metrics: profiler.dump(),
    milestones: profiler.getMilestones(),
  }
}

const send = (final = false) => {
  if (!beaconEnabled) return
  const body = buildBeacon()
  if (Object.keys(body.metrics).length === 0) return
  const blob = new Blob([JSON.stringify(body)], { type: 'application/json' })
  try {
    if (final && navigator.sendBeacon) {
      navigator.sendBeacon('/metrics', blob)
    } else {
      // keep-alive so the request survives even if the user navigates mid-send
      fetch('/metrics', { method: 'POST', body: blob, keepalive: true }).catch(() => { /* silent */ })
    }
  } catch { /* silent */ }
}

if (typeof window !== 'undefined' && beaconEnabled) {
  setInterval(() => send(false), BEACON_INTERVAL_MS)
  window.addEventListener('pagehide', () => send(true), { once: true })
  console.log('[perf] metrics beacon active — POSTing to /metrics every', BEACON_INTERVAL_MS / 1000, 's')
}

export const __beaconSendNow = () => send(false)
if (typeof window !== 'undefined') {
  ;(window as any).__beaconSendNow = __beaconSendNow
}
